import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, Tooltip } from 'antd';
import { StyledSortSelector } from './style';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Divider } from '../../Divider';
import { Icon } from '../../Icon';

export interface SortOption {
  value: string;
  labelId: string;
  disabled?: boolean;
  disabledTooltipId?: string;
}

export interface SortSelectorProps {
  options: SortOption[];
  onChange: (orderBy?: string, direction?: 'asc' | 'desc') => void;
  defaultOrderBy?: string;
  defaultDirection?: 'asc' | 'desc';
  withDivider?: boolean;
}

export const SortSelector = ({
  options,
  onChange,
  defaultOrderBy,
  defaultDirection = 'asc',
  withDivider,
}: SortSelectorProps) => {
  const intl = useIntl();
  const [activeSort, setActiveSort] = useState<string | undefined>(defaultOrderBy);
  const [direction, setDirection] = useState<'asc' | 'desc' | undefined>(
    defaultOrderBy ? defaultDirection : undefined
  );

  useEffect(() => {
    setActiveSort(defaultOrderBy);
    setDirection(defaultOrderBy ? defaultDirection : undefined);
  }, [defaultOrderBy, defaultDirection]);

  const handleSortClick = (value: string) => {
    if (activeSort === value) {
      const newDirection = direction === 'asc' ? 'desc' : 'asc';
      setDirection(newDirection);
      onChange(value, newDirection);
    } else {
      setActiveSort(value);
      setDirection('desc');
      onChange(value, 'desc');
    }
  };

  const menuItems = options.map((option, index) => {
    const isActive = activeSort === option.value;
    const content = (
      <>
        <div className={`sort-menu-item ${option.disabled ? 'disabled' : ''}`}>
          <span className="sort-menu-label">
            {intl.formatMessage({ id: option.labelId })}
          </span>
          {isActive && !option.disabled && (
            <span className="sort-menu-icon">
              {direction === 'asc' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            </span>
          )}
        </div>
        {withDivider && index < options.length - 1 && <Divider marginTop={5} marginBottom={0} />}
      </>
    );

    return {
      key: option.value,
      label: option.disabled && option.disabledTooltipId ? (
        <Tooltip placement={"left"} title={intl.formatMessage({ id: option.disabledTooltipId })}>
          {content}
        </Tooltip>
      ) : (
        content
      ),
      onClick: option.disabled ? undefined : () => handleSortClick(option.value),
      disabled: option.disabled,
    };
  });

  return (
    <StyledSortSelector>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomLeft"
      >
        <div className="sort-dropdown-trigger">
          <span className="sort-dropdown-label">
            {intl.formatMessage({ id: 'general.sort' })}:
          </span>
          <Icon name={'arrow'} />
        </div>
      </Dropdown>
    </StyledSortSelector>
  );
};